import { type PendingTransaction } from '@/features/payouts/admin/PendingTransaction'
import { task } from 'mobx-task'
import { env } from '@/infra/env'
import { links } from '@/utils/link'
import { computed, action } from 'mobx'
import type { ApproveTransactionsFn } from './PayoutDraftingViewModel'

/**
 * Shows the dialog for cancelling a user transaction.
 */
export type ShowCancelUserTransactionFn = (
  transaction: PendingTransaction,
) => void

/**
 * View model for a pending transaction.
 */
export class PendingTransactionViewModel {
  constructor(
    public readonly transaction: PendingTransaction,
    private readonly _selection: Set<PendingTransaction>,
    private readonly _showCancelUserTransaction: ShowCancelUserTransactionFn,
    private readonly _approveTransactions: ApproveTransactionsFn,
  ) {}

  /**
   * Whether the transaction is selected.
   */
  @computed
  get selected() {
    return this._selection.has(this.transaction)
  }

  /**
   * Toggles the selection.
   */
  @action.bound
  toggleSelection() {
    if (this.selected) {
      this._selection.delete(this.transaction)
    } else {
      this._selection.add(this.transaction)
    }
  }

  /**
   * Requests that this transaction be cancelled.
   */
  @action.bound
  requestCancellation() {
    this._showCancelUserTransaction(this.transaction)
  }

  /**
   * Approve the transaction.
   */
  approve = task.resolved(() => this._approveTransactions([this.transaction]))

  /**
   * Views the job in HQ.
   */
  @action.bound
  viewJobInHQ() {
    if (this.transaction.jobId) {
      window.open(`${env.HQ_URL}/jobs/${this.transaction.jobId}`, '_blank')
    }
  }

  /**
   * Views the job in a new window.
   */
  @action.bound
  viewJob() {
    if (this.transaction.jobId) {
      window.open(links.job(this.transaction.jobId), '_blank')
    }
  }
}
